<template>
  <v-form class="pb-10" ref="form" v-model="valid">
    <v-row class="px-5 pt-3">
      <v-col cols="12" lg="6" sm="12" md="6">
        <div class="gen-banner-wrapper">
            <div class="banner-header blue py-4 px-3 white--text">
              <h1 class="font-lg">Generate Banner</h1>
              <p class="font-sm m-t-3">Simply fill out the form with the details of your campaign and we'll generate a unique referral link and a downloadable banner for you.</p>
            </div> 
            <div class="banner-body pa-5">
              <v-row>
                <v-col 
                  cols="12" 
                  lg="12"
                  class="py-0">
                  <p class="label">Campaign Name</p>
                  <v-text-field v-model="campaign_name" :rules="rules.campaign_name" placeholder="Enter Campaign Name" outlined clearable></v-text-field>
                </v-col>

                <v-card
                  class="px-4 pt-4 mx-3 mb-6 w-100"
                  outlined>
                  <div class="campaign-url-container d-flex">
                    <div class="d-flex align-center mr-3">
                      <label class="radio-label">
                        <input type="radio" v-model="tab_choices" value="1" checked="checked" v-on:click="showWebsite" @click:clear="clearApiErrors()" />
                        Website
                      </label>
                    </div>
                    <div class="d-flex align-center">
                      <label class="radio-label">
                        <input type="radio" v-model="tab_choices" value="2" v-on:click="showSocialMedia" />
                        Social Media Domain
                      </label>
                    </div>
                  </div>

                  <v-divider
                    class="my-4"
                  ></v-divider>

                  <div id="websiteinput">
                    <p class="label">Enter Website</p>              
                    <v-text-field @blur="checkingWebsiteUrl()" @keyup="clearApiErrors()" @click:clear="clearApiErrors(), invalid_use_of_url = false" v-model="website" :rules="rules.website" placeholder="Enter Website" outlined clearable></v-text-field>
                    <div v-if="invalid_use_of_url && website !== ''" class="text-left website-validation-wrap">
                      <p class="red--text website-validation">Social Media URL cannot be use in Website.</p>
                    </div>
                    <div v-if="showWebsiteError()" class="text-left website-validation-wrap">
                      <p v-for="(error, index) in apiErrors.website" :key="index" class="red--text website-validation">Website is invalid or has been already taken.</p>
                    </div> 
                  </div>

                  <div id="socialmediadomain" class="hide">
                    <p class="label">Select Social Media Domain</p>              
                    <div class="social-media-url-container d-flex">
                      <v-select v-model="social_media_domain" :items="social_media_domains" @change="composingSocialMediaUrl()" item-text="url" item-value="url" placeholder="Select Social Media Domain" outlined dense class="social_media_domains">
                        <template slot="item" slot-scope="data">
                          <i :class="'fab fa-' + data.item.icon"></i>
                          {{ data.item.url }}
                        </template>
                      </v-select>
                      <div class="d-flex">
                        <p class="slash font-xxl gray-l-text">/</p>
                        <v-text-field v-model="social_media_username" @keyup="composingSocialMediaUrl()" @click:clear="clearApiErrors(), invalid_use_of_username = true" placeholder="Username" outlined clearable></v-text-field>
                      </div>
                    </div>
                    <div class="text-left website-validation-wrap">
                      <p v-for="(error, index) in apiErrors.website" :key="index" class="red--text website-validation">Social media account username has been already taken.</p>
                      <p v-if="social_media_username === '' || invalid_use_of_username" class="red--text website-validation">Username is required</p>
                    </div> 
                  </div>
                </v-card>

                <v-col 
                  cols="12" 
                  lg="6"
                  class="py-0"> 
                    <p class="label">Type</p>  
                    <v-select class="type-cls" v-model="type" :rules="rules.type"  :items="types" placeholder="Select Type" outlined dense></v-select>
                </v-col>  
                <v-col 
                  cols="12" 
                  lg="6"
                  class="py-0"> 
                    <p class="label">Language</p>  
                    <v-select v-model="language" :rules="rules.language" :items="languages" placeholder="Select Language" outlined dense></v-select>
                </v-col>              
                <v-col 
                  cols="12" 
                  lg="6"
                  class="py-0">  
                    <p class="label">Promotion</p>                
                    <v-select v-model="banner" :rules="rules.banner" :items="banners" item-text="name" item-value="id" placeholder="Select Promotion" outlined dense></v-select>
                </v-col>              
                <v-col 
                  cols="12" 
                  lg="6"
                  class="py-0">  
                    <p class="label">Size</p> 
                    <v-select v-model="size" :rules="rules.size" :items="sizes" item-text="name" item-value="id" placeholder="Select Size" outlined dense></v-select>
                </v-col> 
                <v-col 
                  cols="12" 
                  lg="12"
                  class="get-code-wrapper py-0">
                    <p class="label">Referral Code</p>  
                    <v-text-field v-model="referral_code" :rules="rules.referral_code" @blur="checkingWebsiteUrl()" placeholder="Enter Referral Code" outlined clearable></v-text-field>
                    <banner-code-generator @generated="form.referral_code=$event"></banner-code-generator>
                    <div class="text-left website-validation-wrap">
                      <p v-for="(error, index) in apiErrors.referral_code" :key="index" class="red--text website-validation">Referral Code is invalid or has been already taken.</p>
                    </div>                       
                </v-col>
                <v-col 
                  cols="12" 
                  lg="12">  
                    <div class="text-right">
                      <v-btn @click="resetForm(form)" class="mr-4">Reset Form</v-btn>
                      <v-btn @click="saveBanner(form)" :disabled="!valid || invalid_use_of_username" color="success">Generate</v-btn>                                         
                    </div>                
                </v-col>                                                                                                      
              </v-row>
            </div>
        </div>
      </v-col>
      <v-col cols="12" lg="6" sm="12" md="6">
        <div class="banner-header blue py-4 px-3 white--text">
          <h1 class="font-lg">Banner preview</h1>
          <p class="font-sm m-t-3">Banner Preview</p>
        </div>         
        <div class="text-center pa-6 banner-preview grey lighten-4">
          <banner-image-preview v-if="previewedBanner !== null" :banner="previewedBanner"></banner-image-preview>
        </div>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import BannerCodeGenerator from '@/components/affiliate-tools/BannerCodeGenerator'
import BannerPreviewModal from '@/components/affiliate-tools/BannerPreviewModal'
import BannerImagePreview from '@/components/affiliate-tools/BannerImagePreview'

import { mapActions, mapGetters } from 'vuex'
import { createHelpers } from 'vuex-map-fields'
import customValidator from '../../utility/custom-validator'

const { mapFields } = createHelpers({
  getterType: 'affiliateTools/getField',
  mutationType: 'affiliateTools/updateField',
})

const required = (fieldName) => {
  return v => !!v || `${fieldName} is required`
}

export default {
  components: { BannerCodeGenerator, BannerPreviewModal, BannerImagePreview },
  name: 'BannerGenerateForm',
  data: () => ({
    valid: false,
    rules: {
      campaign_name: [ required('Name') ],
      website: [ required('Website') ],
      type: [ required('Type') ],
      banner: [ required('Promotion') ],
      language: [ required('Language') ],
      size: [ required('Size') ],
      referral_code: [ required('Referral Code') ],
    }, 
    types: ['promotion', 'advertisement'],
    languages: ['EN', 'FR', 'DE'],
    social_media_domains: [
      { url: 'https://www.facebook.com/', icon: 'facebook-square' },
      { url: 'https://www.instagram.com/', icon: 'instagram' },
      { url: 'https://twitter.com/', icon: 'twitter' },
      { url: 'https://t.me/', icon: 'telegram' },
      { url: 'https://wa.me/', icon: 'whatsapp' },
      { url: 'https://www.reddit.com/user/', icon: 'reddit' },
    ],
    social_media_domain: 'https://www.facebook.com/',
    social_media_username: '',
    tab_choices: 1,
    invalid_use_of_url: false,
    invalid_use_of_username: false
  }),
  methods: {
    ...mapActions('affiliateTools', [
        'preview', 
        'getBannersForTypeAndLanguage', 
        'saveBanner', 
        'resetForm',
        'clearApiErrors'
      ]
    ),
    submit () {
      this.saveBanner(this.form)
    },
    showWebsite () {
      this.invalid_use_of_url = false;
      document.getElementById('websiteinput').style.display ='block';
      document.getElementById('socialmediadomain').style.display ='none';

      this.form.website = '';
      this.social_media_domain = 'https://www.facebook.com/';
      this.social_media_username = '';
      this.apiErrors.website = '';
    },
    showWebsiteError () {
      return this.invalid_use_of_url === false && this.form.website !== '';
    },
    showSocialMedia () {
      this.invalid_use_of_url = false;
      document.getElementById('socialmediadomain').style.display ='block';
      document.getElementById('websiteinput').style.display ='none';
    },
    composingSocialMediaUrl () {
      this.invalid_use_of_username = false;
      this.form.website = this.social_media_domain + this.social_media_username;
    },
    checkingWebsiteUrl () {
      if (this.tab_choices == 1 && !customValidator.empty(this.form.website)) {
        if (customValidator.isSocialMedia(this.form.website)) {
          this.invalid_use_of_url = true;
          this.valid = false;
        } else {
          this.invalid_use_of_url = false;
          this.valid = true;
        }
      } else {
        this.invalid_use_of_url = false;
      }
    }
  },
  watch: {
    'form.banner': function (newVal, oldVal) {
      if (newVal !== oldVal) this.form.size = ''
    },
    form: {
      handler: function (newVal, oldVal) {
        if (newVal.type && newVal.language) this.getBannersForTypeAndLanguage(newVal)
        if (newVal.banner && newVal.size) this.preview(newVal)
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters('affiliateTools', [ 'banners', 'sizes', 'previewedBanner', 'apiErrors' ]),
    ...mapFields([ 'form.campaign_name', 'form.website', 'form.referral_code', 'form.type', 'form.language', 'form.banner', 'form.size' ]),
    ...mapFields([ 'form' ]),
  }
}
</script>
<style scoped>
  .banner-body{
    border: 1px solid #eaeaea;
    box-shadow: 1px 2px 8px #eaeaea;
  }
  .banner-header{
    border-radius: 15px 15px 0 0;
  }
  .get-code-cls{
    position: absolute;
    right: 12px;
    top: 31px;
    height: 56px;
  }
  .get-code-wrapper {
      position: relative;
  }  
  .get-code-cls{
    position: absolute;
    right: 12px;
    top: 20px;
    height: 40px !important;
    margin: 0 !important;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }  
  .type-cls{
    text-transform: capitalize;      
  }
  .v-select-list {
      text-transform: capitalize;
  }  
  .website-wrap {
    position: relative;
  }
  .website-validation-wrap {
    position: relative;
  }
  .referral-code-wrap{
    position: relative;
  }
  .hide {
    display: none;
  }
  .radio-label {
    font-size: 16px;
    color: rgba(0,0,0,.6);
    cursor: pointer;
  }
  .v-select-list {
    text-transform: initial;
  }
  .v-list-item i {
    font-size: 20px;
    margin-right: 10px;
    color: rgba(0,0,0,.6);
  }
  .campaign-url-container, .social-media-url-container {
    flex-direction: column;
  }
  .campaign-url-container .d-flex {
    margin-bottom: 5px;
  }
  .campaign-url-container .d-flex:last-child {
    margin-bottom: 0;
  }
  .social-media-url-container .slash {
    margin: 5px 12px 5px 0;
  }

  /* Responsive */
  @media only screen and (min-width: 768px) {
    .campaign-url-container, .social-media-url-container {
      flex-direction: row;
    }
    .social-media-url-container .slash {
      margin: 5px 12px;
    }
    .social_media_domains {
      width: 50%;
    }
  }
</style>